import React from "react";

/* ═══════════════════════════════════════════════
   SHARED KIT — one palette, one CSS block, a few
   primitives. Imported by StoreCustomers,
   StoreAnalytics, StoreFinance and StoreGrowth so
   the four tabs can't drift apart.

   Heartlas crimson + espresso + cream. No gold:
   green is reserved for money earned, red for
   money lost.
═══════════════════════════════════════════════ */
export const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",
  blush: "#FDEEF0",
  border: "#EEDFC4",
  divider: "#F3E6CE",
  green: "#1F7A45",
  greenSoft: "#E8F4EC",
};

export const GRAD_RED = `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`;
export const SWEEP = `linear-gradient(90deg, ${HEART.dark}, ${HEART.red}, ${HEART.redDark}, ${HEART.red}, ${HEART.dark})`;

export const money = (n?: number) =>
  `€${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const compact = (n?: number) => Number(n ?? 0).toLocaleString();

export const fmtDay = (ms?: number | null) =>
  !ms ? "—" : new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export const initials = (name?: string) =>
  (name || "?").trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');

  .si{font-family:'DM Sans',sans-serif;color:${HEART.dark};}
  .si *,.si *::before,.si *::after{box-sizing:border-box;}
  .si input,.si button{font-family:inherit;cursor:pointer;}
  .si input::placeholder{color:${HEART.textFaint};}
  .si :focus-visible{outline:2px solid ${HEART.red};outline-offset:3px;border-radius:8px;}

  @keyframes siSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes siRise{from{opacity:0;transform:translateY(14px) scale(.98)}to{opacity:1;transform:none}}
  @keyframes siBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes siGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}

  /* the slab — every surface in these tabs is one */
  .si-slab{
    background:${HEART.panelBg};border:3px solid ${HEART.dark};border-radius:22px;
    box-shadow:0 6px 0 ${HEART.dark},0 14px 26px rgba(27,14,6,.13);
    animation:siRise .34s ease both;overflow:hidden;
  }
  .si-slab.sm{border-width:2.5px;border-radius:18px;box-shadow:0 4px 0 ${HEART.dark};}
  .si-slab.hover:hover{transform:translateY(-4px);box-shadow:0 10px 0 ${HEART.dark},0 20px 34px rgba(27,14,6,.17);}
  .si-slab.hover{transition:transform .16s ease,box-shadow .16s ease;}
  .si-slab.hover:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .si-strip{height:4px;background:${SWEEP};background-size:300% 100%;animation:siSpin 4s linear infinite;}

  .si-h{
    font-family:'Sora',sans-serif;font-weight:900;letter-spacing:-.5px;color:${HEART.dark};
    font-size:20px;margin:0;
  }
  .si-eyebrow{
    font-size:9.5px;font-weight:800;letter-spacing:.2em;color:${HEART.textMuted};
    text-transform:uppercase;margin:0 0 12px;
  }
  .si-num{font-family:'Sora',sans-serif;font-weight:900;letter-spacing:-.6px;line-height:1;}

  .si-btn{
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:8px 15px;border-radius:999px;font-size:12px;font-weight:800;
    font-family:'Sora',sans-serif;white-space:nowrap;
    border:2.5px solid ${HEART.dark};background:${HEART.panelBg};color:${HEART.dark};
    box-shadow:0 4px 0 ${HEART.dark};transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .si-btn:hover{transform:translateY(-2px);box-shadow:0 6px 0 ${HEART.dark};}
  .si-btn:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .si-btn.gr{background:${GRAD_RED};color:#fff;}
  .si-btn.bl{background:${HEART.blush};color:${HEART.dark};}
  .si-btn.gn{background:${HEART.greenSoft};color:${HEART.green};}
  .si-btn.sm{padding:5px 11px;font-size:10.5px;border-width:2px;box-shadow:0 3px 0 ${HEART.dark};}
  .si-btn.sm:hover{box-shadow:0 5px 0 ${HEART.dark};}
  .si-btn.sm:active{box-shadow:0 1px 0 ${HEART.dark};}
  .si-btn.ico{width:32px;height:32px;padding:0;border-radius:11px;}

  .si-pill{
    display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;
    border:2px solid ${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    font-family:'Sora',sans-serif;font-size:10px;font-weight:800;flex-shrink:0;
  }

  .si-search{
    display:flex;align-items:center;flex:1;background:${HEART.panelBg};border-radius:14px;
    overflow:hidden;border:2px solid ${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    transition:box-shadow .16s ease;
  }
  .si-search:focus-within{box-shadow:0 3px 0 ${HEART.red};}
  .si-search input{flex:1;background:none;border:none;outline:none;font-size:12.5px;font-weight:600;
    color:${HEART.dark};padding:9px 12px 9px 0;}

  .si-grid{display:grid;gap:14px;}
  .si-row{
    display:flex;align-items:center;gap:12px;padding:12px 14px;
    border-bottom:1px solid ${HEART.divider};
  }
  .si-row:last-child{border-bottom:none;}

  .si-avatar{
    width:40px;height:40px;border-radius:13px;flex-shrink:0;
    display:flex;align-items:center;justify-content:center;
    border:2.5px solid ${HEART.dark};background:${HEART.blush};
    font-family:'Sora',sans-serif;font-weight:900;font-size:13px;color:${HEART.redDark};
  }

  .si-rank{
    width:26px;height:26px;border-radius:9px;flex-shrink:0;
    display:flex;align-items:center;justify-content:center;
    background:${HEART.dark};color:#fff;
    font-family:'Sora',sans-serif;font-weight:900;font-size:11px;
  }

  /* bars — hard-edged, bordered, same physical language as the slabs */
  .si-track{height:12px;border-radius:999px;border:2px solid ${HEART.dark};background:${HEART.bg};overflow:hidden;}
  .si-fill{height:100%;background:${GRAD_RED};transition:width .4s ease;}
  .si-fill.gn{background:${HEART.green};}

  .si-col{
    flex:1;min-width:3px;border-radius:4px 4px 0 0;background:${GRAD_RED};
    border:1.5px solid ${HEART.dark};border-bottom:none;
    transform-origin:bottom;animation:siGrow .5s ease both;
  }

  .si-empty{text-align:center;padding:40px 24px;}

  @media (prefers-reduced-motion: reduce){
    .si-slab,.si-btn,.si-col{animation:none!important;transition:none!important;}
  }
`;

/** Drop once per tab. */
export const InsightsStyles: React.FC = () => <style>{CSS}</style>;

/** Big number on a slab. */
export const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "green" | "red";
  delay?: number;
}> = ({ icon, label, value, sub, tone = "default", delay = 0 }) => {
  const colour = tone === "green" ? HEART.green : tone === "red" ? HEART.red : HEART.dark;
  return (
    <div className="si-slab" style={{ padding: "16px 16px 14px", animationDelay: `${delay * 55}ms` }}>
      <div style={{
        width: 32, height: 32, borderRadius: 11, background: HEART.blush,
        border: `2px solid ${HEART.dark}`, display: "flex", alignItems: "center",
        justifyContent: "center", color: HEART.red, marginBottom: 12,
      }}>
        {icon}
      </div>
      <div className="si-num" style={{ fontSize: 24, color: colour }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: HEART.textMuted, marginTop: 5 }}>{label}</div>
      {sub && <div style={{ fontSize: 10.5, color: HEART.textFaint, fontWeight: 600, marginTop: 2 }}>{sub}</div>}
    </div>
  );
};

/** Section heading with the animated strip under it. */
export const SectionHead: React.FC<{ title: string; right?: React.ReactNode }> = ({ title, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
    <h3 className="si-h">{title}</h3>
    {right}
  </div>
);

export const EmptyState: React.FC<{ emoji: string; title: string; body: string }> = ({ emoji, title, body }) => (
  <div className="si-slab si-empty">
    <div style={{ fontSize: 44, marginBottom: 12, animation: "siBob 3.2s ease-in-out infinite" }}>{emoji}</div>
    <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 18, margin: "0 0 6px", letterSpacing: -.4 }}>{title}</p>
    <p style={{ fontSize: 13, color: HEART.textMuted, fontWeight: 600, margin: 0, maxWidth: 340, marginInline: "auto", lineHeight: 1.6 }}>{body}</p>
  </div>
);

/** ±% chip. Null means there's no prior period to compare against. */
export const DeltaChip: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) {
    return (
      <span className="si-pill" style={{ background: HEART.bg, color: HEART.textMuted }}>
        No prior data
      </span>
    );
  }
  const up = pct >= 0;
  return (
    <span className="si-pill" style={{
      background: up ? HEART.greenSoft : HEART.redSoft,
      color: up ? HEART.green : HEART.redDark,
    }}>
      {up ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
};
